<script setup lang="ts">
import Dialog from 'primevue/dialog'
import { ProductType } from 'shared/products/domain/productType';
import Button from 'primevue/button';
import { computed, ref, watch } from 'vue';
import Fieldset from 'primevue/fieldset';
import SelectButton from 'primevue/selectbutton'
import ColorPickerField from 'shared/forms/components/ColorPickerField.vue';
import TextField from 'shared/forms/components/TextField.vue';
import SelectField from 'shared/forms/components/SelectField.vue';
import TextareaField from 'shared/forms/components/TextareaField.vue';
import NumberField from 'shared/forms/components/NumberField.vue';
import MeasurementInput from '@/common/components/MeasurementInput.vue';
import ProblemDetailsView from 'shared/problems/components/ProblemDetailsView.vue';
import { useUpdateProduct, type UpdateProduct } from '@/products/api/updateProduct';
import { useCreateProduct, type CreateProduct } from '../api/createProduct';
import { buildProductFormValues, type ProductFormValues } from './copyProduct';
import type { ProblemDetails } from "shared/problems/domain/problemDetails";
import { Assert } from 'shared/assert';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { useProductStore } from 'shared/products/stores/productStore';
import { useRefereshProductStore } from '../commands/refreshProductStore';
import type { Product } from 'shared/products/domain/product';
import type { DimensionData } from 'shared/products/domain/product';
import { useProductGroupStore } from 'shared/products/groups/stores/productGroupStore';
import { useRefereshProductGroupStore } from '../groups/commands/refreshProductGroupStore';
import type { ProductGroup } from 'shared/products/groups/domain/productGroup';
import ProgressBar from 'primevue/progressbar';
import { Unit } from 'shared/common/domain/bulkUnits';
import { useFeatureFlagStore } from '@/featureFlags/stores/featureFlagStore';

defineExpose({
  show,
  editMultipleProducts,
});

const productStore = useProductStore();
const createProduct = useCreateProduct();
const updateProduct = useUpdateProduct();
const languageStore = useLanguageStore();
const refreshProductStore = useRefereshProductStore();
const refereshProductGroupStore = useRefereshProductGroupStore();
const { translations, language } = storeToRefs(languageStore);
const productGroupStore = useProductGroupStore();
const featureFlags = useFeatureFlagStore();

const loading = ref(false);
const problems = ref<ProblemDetails>();
const progress = ref(0);
const showProgressBar = ref(false);

const types = ref<ProductType[]>([]);

if (featureFlags.isLogYardEnabled()) types.value.push(ProductType.Log);
if (featureFlags.isBulkEnabled()) types.value.push(ProductType.Bulk);
if (featureFlags.isCoilEnabled()) types.value.push(ProductType.Coil);
if (featureFlags.isPackagesEnabled()) types.value.push(ProductType.Dimension);

const typeOptions = computed<TypeOption[]>(() =>
  types.value
    .map((t) => ({
      value: t,
      name: translations.value.productType(t)
    }))
    .sort((a, b) => a.name.localeCompare(b.name, language.value.code, { sensitivity: 'base' }))
);

const defaultType = typeOptions.value.find(o => o.value === ProductType.Log) ?? typeOptions.value[0]!;

const operation = ref<'create' | 'edit' | 'editMultiple' | 'none'>('none');
const visible = ref<boolean>();

const current = ref<Product>();

const title = ref('');

const editProducts = ref<Product[]>([]);

const name = ref<string | undefined>();
const nameChanged = computed(() => {
  if (operation.value === 'create' || operation.value === 'none') return false;
  const product = current.value;
  if (!product) return false;
  if (operation.value == 'editMultiple') return false;
  return name.value !== product.name;
});

const nameError = computed(() => {
  if (operation.value === 'editMultiple') return undefined
  if (name.value === '' || name.value == undefined) {
    return translations.value.error.required;
  }

  if (name.value && name.value.length > 100) {
    return translations.value.error.maxLength(100);
  }

  return undefined;
});



const code = ref<string | undefined>();
const codeChanged = computed(() => {
  if (operation.value === 'create' || operation.value === 'none') return false;
  if (operation.value === 'editMultiple') return code.value !== undefined;
  const product = current.value;
  if (!product) return false;
  return (code.value ?? '') !== (product.externalCode ?? '');
});

const quality = ref<string | undefined>();
const qualityChanged = computed(() => {
  if (operation.value === 'create' || operation.value === 'none') return false;
  if (operation.value === 'editMultiple') return code.value !== undefined;
  const product = current.value;
  if (!product) return false;
  return (quality.value ?? '') !== (product.quality ?? '');
});

const color = ref<string | undefined>();
const colorChanged = computed(() => {
  if (operation.value === 'create' || operation.value === 'none') return false;
  if (operation.value === 'editMultiple') return color.value !== undefined;
  const product = current.value;
  if (!product) return false;
  return (color.value ?? null) !== product.color;
});

const description = ref<string | undefined>();
const descriptionChanged = computed(() => {
  if (operation.value === 'create' || operation.value === 'none') return false;
  if (operation.value === 'editMultiple') return description.value !== undefined;
  const product = current.value;
  if (!product) return false;
  return description.value !== product.description;
});

const selectedType = ref<TypeOption>(defaultType);
const selectedTypeChanged = computed(() => {
  if (operation.value !== 'edit') return false;
  const product = current.value;
  if (!product) return false;
  return selectedType.value.value !== product.type;
});


const selectedProductGroup = ref<ProductGroup | null | undefined>(undefined);
const selectedProductGroupChanged = computed(() => {
  if (operation.value === 'create' || operation.value === 'none') return false;
  if (operation.value === 'editMultiple') return selectedProductGroup.value !== undefined;
  const product = current.value;
  if (!product) return false;
  return (selectedProductGroup.value?.id ?? null) !== product.productGroupId;
});

const groups = computed(() => {

  if (selectedType.value.value === ProductType.Undefined) {
    return productGroupStore.sortedByName;
  }

  return productGroupStore.sortedByName.filter(p => p.productType === selectedType.value.value);
});

const logsPerSquareMeter = ref<number | undefined>();
const logsPerSquareMeterChanged = computed(() => {
  if (operation.value === 'create' || operation.value === 'none') return false;
  if (operation.value === 'editMultiple') return logsPerSquareMeter.value !== undefined;
  const product = current.value;
  if (!product || !product.logData) return false;
  return logsPerSquareMeter.value !== product.logData.logsPerSquareMeter;
});
const logsPerSquareMeterError = computed(() => {
  if (operation.value === 'editMultiple' && logsPerSquareMeter.value === undefined) return undefined
  if (logsPerSquareMeter.value === undefined || logsPerSquareMeter.value === null) {
    return translations.value.error.required;
  }

  return undefined;
});

interface ToggleOptions {
  label: string,
  value: boolean
}

const isSortedOptions = computed<ToggleOptions[]>(() => [
  {
    label: translations.value.generic.sorted,
    value: true
  },
  {
    label: translations.value.generic.unsorted,
    value: false
  }
]);

const isSorted = ref<boolean | undefined>();
const isSortedChanged = computed(() => {
  if (operation.value === 'create' || operation.value === 'none') return false;
  if (operation.value === 'editMultiple') return isSorted.value !== undefined;
  const product = current.value;
  if (!product || !product.logData) return false;
  return isSorted.value !== product.logData.isSorted;
});

const species = ref<string | undefined>();
const speciesChanged = computed(() => {
  if (operation.value === 'create' || operation.value === 'none') return false;
  if (operation.value === 'editMultiple') return species.value !== undefined;
  const product = current.value;
  if (!product || !product.logData) return false;
  return (species.value ?? null) !== product.logData.species;
});

const minDiameter = ref<number | undefined>();
const minDiameterChanged = computed(() => {
  if (operation.value === 'create' || operation.value === 'none') return false;
  if (operation.value === 'editMultiple') return minDiameter.value !== undefined;
  const product = current.value;
  if (!product || !product.logData) return false;
  return (minDiameter.value ?? null) !== product.logData.minDiameter;
});

const maxDiameter = ref<number | undefined>();
const maxDiameterChanged = computed(() => {
  if (operation.value === 'create' || operation.value === 'none') return false;
  if (operation.value === 'editMultiple') return maxDiameter.value !== undefined;
  const product = current.value;
  if (!product) return false;
  if (!product.logData) return false;
  return (maxDiameter.value ?? null) !== product.logData.maxDiameter;
});

const minLength = ref<number | undefined>();
const minLengthChanged = computed(() => {
  if (operation.value === 'create' || operation.value === 'none') return false;
  if (operation.value === 'editMultiple') return minLength.value !== undefined;
  const product = current.value;
  if (!product || !product.logData) return false;
  return (minLength.value ?? null) !== product.logData.minLength;
});

const maxLength = ref<number | undefined>();
const maxLengthChanged = computed(() => {
  if (operation.value === 'create' || operation.value === 'none') return false;
  if (operation.value === 'editMultiple') return maxLength.value !== undefined;
  const product = current.value;
  if (!product || !product.logData) return false;
  return (maxLength.value ?? null) !== product.logData.maxLength;
});

const averageCylinderVolume = ref<number | undefined>();
const averageCylinderVolumeChanged = computed(() => {
  if (operation.value === 'create' || operation.value === 'none') return false;
  if (operation.value === 'editMultiple') return averageCylinderVolume.value !== undefined;
  const product = current.value;
  if (!product || !product.logData) return false;
  return (averageCylinderVolume.value ?? null) !== product.logData.averageCylinderVolume;
});

const averagePhysicalVolume = ref<number | undefined>();
const averagePhysicalVolumeChanged = computed(() => {
  if (operation.value === 'create' || operation.value === 'none') return false;
  if (operation.value === 'editMultiple') return averagePhysicalVolume.value !== undefined;
  const product = current.value;
  if (!product || !product.logData) return false;
  return (averagePhysicalVolume.value ?? null) !== product.logData?.averagePhysicalVolume;
});

const logSorterName = ref<string | undefined>();
const logSorterNameChanged = computed(() => {
  if (operation.value === 'create' || operation.value === 'none') return false;
  if (operation.value === 'editMultiple') return logSorterName.value !== undefined;
  const product = current.value;
  if (!product || !product.logData) return false;
  return (logSorterName.value ?? null) !== product.logData.logSorterName;
});

const sawLineName = ref<string | undefined>();
const sawLineNameChanged = computed(() => {
  if (operation.value === 'create' || operation.value === 'none') return false;
  if (operation.value === 'editMultiple') return sawLineName.value !== undefined;
  const product = current.value;
  if (!product || !product.logData) return false;
  return (sawLineName.value ?? null) !== product.logData.sawLineName;
});

// COILS
const diameter = ref<number | undefined>();
const diameterChanged = computed(() => {
  if (operation.value === 'create' || operation.value === 'none') return false;
  if (operation.value === 'editMultiple') return diameter.value !== undefined;
  const product = current.value;
  if (!product || !product.coilData) return false;
  return (diameter.value ?? null) !== product.coilData.diameter;
});

const depth = ref<number | undefined>();
const depthChanged = computed(() => {
  if (operation.value === 'create' || operation.value === 'none') return false;
  if (operation.value === 'editMultiple') return depth.value !== undefined;
  const product = current.value;
  if (!product || !product.coilData) return false;
  return (depth.value ?? null) !== product.coilData.depth;
});

//BULK

const bulkUnitOptions = computed(() => Object.values(Unit).filter(v => typeof v === 'number').map((unit) => {
  return {
    value: unit,
    name: translations.value.bulk.bulkUnit(unit)
  };
}));

const defaultBulkUnit = bulkUnitOptions.value.find(o => o.value === Unit.MetricTon) ?? bulkUnitOptions.value[0]!;

const bulkUnit = ref<BulkUnitOption | undefined>(defaultBulkUnit);
watch(bulkUnitOptions, (options) => {
  bulkUnit.value = options.find(o => o.value === bulkUnit.value?.value);
});

const bulkUnitChanged = computed(() => {
  if (operation.value === 'create' || operation.value === 'none') return false;
  if (operation.value === 'editMultiple') return bulkUnit.value !== undefined;
  const product = current.value;
  if (!product) return false;
  return bulkUnit.value?.value !== product.bulkData?.unit;
});

const bulkDensity = ref<number>();
const bulkDensityChanged = computed(() => {
  if (operation.value === 'create' || operation.value === 'none') return false;
  if (operation.value === 'editMultiple') return bulkDensity.value !== undefined;
  const product = current.value;
  if (!product || !product.bulkData) return false;
  return (bulkDensity.value ?? null) !== product.bulkData.density;
});

const densityError = computed(() => {
  if (operation.value === 'editMultiple') return undefined
  if (!bulkDensity.value || bulkDensity.value < 0.1) {
    return translations.value.error.invalidDensity;
  }
  return undefined;
});

// DIMENSION
const dimMoisture = ref<number | undefined>();
const dimNominalDensity = ref<number | undefined>();
const dimDensity = ref<number | undefined>();
const dimNominalThickness = ref<number | undefined>();
const dimThickness = ref<number | undefined>();
const dimNominalWidth = ref<number | undefined>();
const dimWidth = ref<number | undefined>();
const dimNominalLength = ref<number | undefined>();
const dimLength = ref<number | undefined>();

function buildDimensionData(): DimensionData {
  return {
    nominalDensity: dimNominalDensity.value ?? null,
    density: dimDensity.value ?? null,
    nominalThickness: dimNominalThickness.value ?? null,
    thickness: dimThickness.value ?? null,
    nominalWidth: dimNominalWidth.value ?? null,
    width: dimWidth.value ?? null,
    nominalLength: dimNominalLength.value ?? null,
    length: dimLength.value ?? null,
    moisture: dimMoisture.value ?? null,
  };
}

const dimensionChanged = computed(() => {
  if (operation.value === 'create' || operation.value === 'none') return false;
  const product = current.value;
  if (!product || !product.dimensionData) return false;
  const d = product.dimensionData;
  return (dimNominalDensity.value ?? null) !== d.nominalDensity ||
    (dimDensity.value ?? null) !== d.density ||
    (dimNominalThickness.value ?? null) !== d.nominalThickness ||
    (dimThickness.value ?? null) !== d.thickness ||
    (dimNominalWidth.value ?? null) !== d.nominalWidth ||
    (dimWidth.value ?? null) !== d.width ||
    (dimNominalLength.value ?? null) !== d.nominalLength ||
    (dimLength.value ?? null) !== d.length ||
    (dimMoisture.value ?? null) !== d.moisture;
});


const valid = computed(() => {

  if (nameError.value !== undefined) return false;

  if (selectedType.value.value === ProductType.Log) {
    if (logsPerSquareMeterError.value !== undefined) return false;
  }

  if (selectedType.value.value === ProductType.Bulk) {
    if (densityError.value !== undefined) return false;
  }

  return true;
});

const hasChanges = computed(() => {
  return nameChanged.value ||
    codeChanged.value ||
    qualityChanged.value ||
    colorChanged.value ||
    descriptionChanged.value ||
    selectedTypeChanged.value ||
    selectedProductGroupChanged.value ||
    logsPerSquareMeterChanged.value ||
    isSortedChanged.value ||
    speciesChanged.value ||
    minDiameterChanged.value ||
    maxDiameterChanged.value ||
    minLengthChanged.value ||
    maxLengthChanged.value ||
    averageCylinderVolumeChanged.value ||
    averagePhysicalVolumeChanged.value ||
    logSorterNameChanged.value ||
    sawLineNameChanged.value ||
    diameterChanged.value ||
    depthChanged.value ||
    bulkUnitChanged.value ||
    bulkDensityChanged.value ||
    dimensionChanged.value;
});

const saveEnabled = computed(() => {
  return valid.value &&
    loading.value == false &&
    progress.value == 0 &&
    (operation.value === 'create' || (operation.value === 'edit' && hasChanges.value) || (operation.value === 'editMultiple' && hasChanges.value))
});

interface TypeOption {
  name: string;
  value: ProductType;
}

interface BulkUnitOption {
  name: string;
  value: Unit;
}

interface ShowDialog {
  productId?: string;
  copyProductId?: string;
}

async function show(options: ShowDialog) {
  try {
    loading.value = true;
    problems.value = undefined;

    await refereshProductGroupStore.refresh();
    if (options.copyProductId !== undefined) {
      await showCopy(options.copyProductId);
    } else if (options.productId === undefined) {
      showCreate();
    } else {
      await showEdit(options.productId);
    }

  } finally {
    loading.value = false
    showProgressBar.value = false;
  }
}

function editMultipleProducts(productIds: string[], productType: ProductType) {
  if (productIds.length == 0) return;
  title.value = translations.value.products.editMultipleProducts(productIds.length);
  clearFields();
  selectedType.value = { name: translations.value.productType(productType), value: productType };

  editProducts.value = [];
  productIds.forEach(id => {
    const product = productStore.getById(id)
    if (product != undefined) {
      editProducts.value.push(product);
    }
  });

  operation.value = 'editMultiple';
  visible.value = true;

}


function clearFields() {
  name.value = undefined;
  code.value = undefined;
  quality.value = undefined;
  description.value = undefined;
  color.value = undefined;
  selectedType.value = defaultType;

  logsPerSquareMeter.value = undefined;
  isSorted.value = undefined;
  species.value = undefined;
  minDiameter.value = undefined;
  maxDiameter.value = undefined;
  minLength.value = undefined;
  maxLength.value = undefined;
  averageCylinderVolume.value = undefined;
  averagePhysicalVolume.value = undefined;
  logSorterName.value = undefined;
  sawLineName.value = undefined;
  depth.value = undefined;
  diameter.value = undefined;
  selectedProductGroup.value = undefined;

  bulkUnit.value = undefined;
  bulkDensity.value = undefined;

  dimMoisture.value = undefined;
  dimNominalDensity.value = undefined;
  dimDensity.value = undefined;
  dimNominalThickness.value = undefined;
  dimThickness.value = undefined;
  dimNominalWidth.value = undefined;
  dimWidth.value = undefined;
  dimNominalLength.value = undefined;
  dimLength.value = undefined;

};

function showCreate() {
  title.value = translations.value.products.addNewProduct;
  clearFields();

  current.value = undefined;
  operation.value = 'create';
  visible.value = true;
}

async function showEdit(id: string) {
  title.value = translations.value.products.editProduct;
  await refreshProductStore.initialize();
  const product = productStore.getById(id);
  Assert.definied(product);

  applyFormValues(buildProductFormValues(product, (n) => n));

  operation.value = 'edit';
  current.value = product;
  visible.value = true;
}

async function showCopy(id: string) {
  title.value = translations.value.products.addNewProduct;
  await refreshProductStore.initialize();
  const product = productStore.getById(id);
  Assert.definied(product);

  clearFields();
  applyFormValues(buildProductFormValues(product, translations.value.generic.copyOfName));

  operation.value = 'create';
  current.value = undefined;
  visible.value = true;
}

function applyFormValues(values: ProductFormValues) {
  name.value = values.name;
  code.value = values.code;
  quality.value = values.quality;
  color.value = values.color;
  description.value = values.description;
  selectedType.value = typeOptions.value.find(o => o.value === values.type) ?? defaultType;
  selectedProductGroup.value = groups.value.find(g => g.id === values.productGroupId) ?? undefined;

  const logData = values.logData;

  if (logData) {
    logsPerSquareMeter.value = logData.logsPerSquareMeter;
    isSorted.value = logData.isSorted;
    species.value = logData.species ?? undefined;
    minDiameter.value = logData.minDiameter ?? undefined;
    maxDiameter.value = logData.maxDiameter ?? undefined;
    minLength.value = logData.minLength ?? undefined;
    maxLength.value = logData.maxLength ?? undefined;
    averageCylinderVolume.value = logData.averageCylinderVolume ?? undefined;
    averagePhysicalVolume.value = logData.averagePhysicalVolume ?? undefined;
    logSorterName.value = logData.logSorterName ?? undefined;
    sawLineName.value = logData.sawLineName ?? undefined;
  }

  const coilData = values.coilData;

  if (coilData) {
    diameter.value = coilData.diameter ?? undefined;
    depth.value = coilData.depth ?? undefined;
  }

  const bulkData = values.bulkData;

  if (bulkData) {
    bulkUnit.value = bulkUnitOptions.value.find(o => o.value === bulkData.unit) ?? undefined;
    bulkDensity.value = bulkData.density ?? undefined;
  }

  const dimensionData = values.dimensionData;

  if (dimensionData) {
    dimMoisture.value = dimensionData.moisture ?? undefined;
    dimNominalDensity.value = dimensionData.nominalDensity ?? undefined;
    dimDensity.value = dimensionData.density ?? undefined;
    dimNominalThickness.value = dimensionData.nominalThickness ?? undefined;
    dimThickness.value = dimensionData.thickness ?? undefined;
    dimNominalWidth.value = dimensionData.nominalWidth ?? undefined;
    dimWidth.value = dimensionData.width ?? undefined;
    dimNominalLength.value = dimensionData.nominalLength ?? undefined;
    dimLength.value = dimensionData.length ?? undefined;
  }
}

async function onSubmit() {
  if (!valid.value) return;

  try {
    loading.value = true;
    if (operation.value === 'create') {
      await create();
    } else if (operation.value === 'edit') {
      const id = current.value?.id;
      Assert.definied(id);
      await update(id);
    } else if (operation.value === 'editMultiple') {
      await updateMultiple();
    }
  } finally {
    loading.value = false;
  }
}

async function create() {
  const product: CreateProduct = {
    name: name.value ?? '',
    description: description.value ?? '',
    externalCode: code.value ?? null,
    quality: quality.value ?? null,
    type: selectedType.value.value,
    color: color.value ?? null,
    logData: null,
    coilData: null,
    bulkData: null,
    dimensionData: null,
    productGroupId: selectedProductGroup.value?.id ?? null
  };

  if (product.type === ProductType.Log) {
    product.logData = {
      averageCylinderVolume: averageCylinderVolume.value ?? null,
      averagePhysicalVolume: averagePhysicalVolume.value ?? null,
      isSorted: isSorted.value ?? false,
      species: species.value ?? null,
      logSorterName: logSorterName.value ?? null,
      logsPerSquareMeter: logsPerSquareMeter.value ?? 0,
      maxDiameter: maxDiameter.value ?? null,
      maxLength: maxLength.value ?? null,
      minDiameter: minDiameter.value ?? null,
      minLength: minLength.value ?? null,
      sawLineName: sawLineName.value ?? null
    };
  }

  if (product.type === ProductType.Coil) {
    product.coilData = {
      depth: depth.value ?? null,
      diameter: diameter.value ?? null
    };
  }

  if (product.type === ProductType.Bulk) {
    Assert.definied(bulkUnit.value);
    Assert.definied(bulkDensity.value);
    if (bulkDensity.value < 0.1) return;
    product.bulkData = {
      unit: bulkUnit.value.value,
      density: bulkDensity.value
    };
  }

  if (product.type === ProductType.Dimension) {
    product.dimensionData = buildDimensionData();
  }

  const response = await createProduct.execute({ entity: product });

  if (!response.ok) {
    problems.value = response.details;
    return;
  }
  loading.value = false;
  operation.value = 'none';
  visible.value = false;
}

async function update(id: string) {

  const product: UpdateProduct = {
    name: name.value ?? '',
    description: description.value ?? '',
    externalCode: code.value ?? null,
    quality: quality.value ?? null,
    type: selectedType.value.value,
    logData: null,
    coilData: null,
    bulkData: null,
    dimensionData: null,
    color: color.value ?? null,
    productGroupId: selectedProductGroup.value?.id ?? null
  };

  if (product.type === ProductType.Log) {
    product.logData = {
      averageCylinderVolume: averageCylinderVolume.value ?? null,
      averagePhysicalVolume: averagePhysicalVolume.value ?? null,
      isSorted: isSorted.value ?? false,
      species: species.value ?? null,
      logSorterName: logSorterName.value ?? null,
      logsPerSquareMeter: logsPerSquareMeter.value ?? 0,
      maxDiameter: maxDiameter.value ?? null,
      maxLength: maxLength.value ?? null,
      minDiameter: minDiameter.value ?? null,
      minLength: minLength.value ?? null,
      sawLineName: sawLineName.value ?? null
    };
  }

  if (product.type === ProductType.Coil) {
    product.coilData = {
      depth: depth.value ?? null,
      diameter: diameter.value ?? null
    };
  }

  if (product.type === ProductType.Bulk) {
    Assert.definied(bulkUnit.value);
    Assert.definied(bulkDensity.value);
    if (bulkDensity.value < 0.1) return;
    product.bulkData = {
      unit: bulkUnit.value?.value,
      density: bulkDensity.value
    };
  }

  if (product.type === ProductType.Dimension) {
    product.dimensionData = buildDimensionData();
  }

  const response = await updateProduct.execute({ id: id, entity: product });

  if (!response.ok) {
    problems.value = response.details;
    return;
  }

  current.value = undefined;
  operation.value = 'none';
  visible.value = false;
}

async function updateMultiple() {
  progress.value = 0;
  showProgressBar.value = true;
  for (let i = 0; i < editProducts.value.length; i++) {
    const product = editProducts.value[i];
    if (product) {
      const changedProduct: UpdateProduct = {
        name: product.name,
        description: description.value !== undefined ? description.value : product.description,
        externalCode: code.value !== undefined ? code.value : product.externalCode,
        quality: quality.value !== undefined ? quality.value : product.quality,
        type: product.type,
        logData: null,
        coilData: null,
        bulkData: null,
        dimensionData: null,
        color: color.value !== undefined ? color.value : product.color,
        productGroupId: selectedProductGroup.value !== undefined ? selectedProductGroup.value?.id ?? null : product.productGroupId
      };

      if (product.logData !== null && product.type === ProductType.Log) {
        changedProduct.logData = {
          logsPerSquareMeter: logsPerSquareMeter.value !== undefined ? logsPerSquareMeter.value : product.logData.logsPerSquareMeter,
          minDiameter: minDiameter.value !== undefined ? minDiameter.value : product.logData.minDiameter,
          maxDiameter: maxDiameter.value !== undefined ? maxDiameter.value : product.logData.maxDiameter,
          maxLength: maxLength.value !== undefined ? maxLength.value : product.logData.maxLength,
          minLength: minLength.value !== undefined ? minLength.value : product.logData.minLength,
          averageCylinderVolume: averageCylinderVolume.value !== undefined ? averageCylinderVolume.value : product.logData.averageCylinderVolume,
          averagePhysicalVolume: averagePhysicalVolume.value !== undefined ? averagePhysicalVolume.value : product.logData.averagePhysicalVolume,
          isSorted: isSorted.value !== undefined ? isSorted.value : product.logData.isSorted,
          species: species.value !== undefined ? species.value : product.logData.species,
          logSorterName: logSorterName.value !== undefined ? logSorterName.value : product.logData.logSorterName,
          sawLineName: sawLineName.value !== undefined ? sawLineName.value : product.logData.sawLineName
        };
      }

      if (product.coilData !== null && product.type === ProductType.Coil) {
        changedProduct.coilData = {
          depth: depth.value !== undefined ? depth.value : product.coilData.depth,
          diameter: diameter.value !== undefined ? diameter.value : product.coilData.diameter
        };
      }

      if (product.bulkData !== null && product.type === ProductType.Bulk) {
        changedProduct.bulkData = {
          unit: bulkUnit.value !== undefined ? bulkUnit.value.value : product.bulkData.unit,
          density: bulkDensity.value !== undefined ? bulkDensity.value : product.bulkData.density
        };
      }

      if (product.dimensionData !== null && product.type === ProductType.Dimension) {
        const d = product.dimensionData;
        changedProduct.dimensionData = {
          nominalDensity: dimNominalDensity.value !== undefined ? dimNominalDensity.value : d.nominalDensity,
          density: dimDensity.value !== undefined ? dimDensity.value : d.density,
          nominalThickness: dimNominalThickness.value !== undefined ? dimNominalThickness.value : d.nominalThickness,
          thickness: dimThickness.value !== undefined ? dimThickness.value : d.thickness,
          nominalWidth: dimNominalWidth.value !== undefined ? dimNominalWidth.value : d.nominalWidth,
          width: dimWidth.value !== undefined ? dimWidth.value : d.width,
          nominalLength: dimNominalLength.value !== undefined ? dimNominalLength.value : d.nominalLength,
          length: dimLength.value !== undefined ? dimLength.value : d.length,
          moisture: dimMoisture.value !== undefined ? dimMoisture.value : d.moisture,
        };
      }

      const response = await updateProduct.execute({ id: product.id, entity: changedProduct });
      progress.value++;
      if (!response.ok) {
        problems.value = response.details;
      }
    }
  };
  setTimeout(() => {
    //loading.value = false
    visible.value = false;
    editProducts.value = [];
    operation.value = 'none';
    progress.value = 0;
    showProgressBar.value = false;
  }, 500);
};

function onHide() {
  editProducts.value = [];
  current.value = undefined;
  operation.value = 'none';
  visible.value = false;
  progress.value = 0;
  showProgressBar.value = false;
}

function calcLogsPerM2() {

  const minDia = minDiameter.value != undefined ? minDiameter.value : current.value?.logData?.minDiameter ?? null
  const maxDia = maxDiameter.value != undefined ? maxDiameter.value : current.value?.logData?.maxDiameter ?? null

  if (minDia == null || maxDia == null) return;

  // Medel dia
  const meanDia = (minDia + maxDia) / 2;

  // Medel mitt dia
  const meanMidDia = Math.sqrt((meanDia * meanDia) / 0.73);

  // St.änd. Area av tot gr.area
  const numerator = Math.pow(meanMidDia / 2, 2) * Math.PI;
  const sqrtPart = Math.pow(Math.pow(meanMidDia / 2, 2) - Math.pow(meanMidDia / 4, 2), 0.5);
  const denominator = ((sqrtPart * (meanMidDia / 4)) - ((Math.pow(meanMidDia / 2, 2) * Math.PI) / 6 - sqrtPart * (meanMidDia / 4)) * 3) / 3 * 6 + Math.pow(meanMidDia / 2, 2) * Math.PI;
  const result = numerator / denominator;

  const correctionAgainstCountedAmount = 0.9
  const logsPerM2 = Math.floor((1 / (Math.pow(meanMidDia / 2000, 2) * Math.PI)) * result * correctionAgainstCountedAmount);

  logsPerSquareMeter.value = isNaN(logsPerM2) ? undefined : logsPerM2
}

</script>

<template>
  <Dialog modal v-model:visible="visible" :header="title" @hide="onHide">
    <ProblemDetailsView v-model="problems"></ProblemDetailsView>
    <div class="product-form">
      <TextField :autofocus="true" :title="translations.generic.name" v-model:model-value="name" :changed="nameChanged"
        :error="nameError" :disabled="operation === 'editMultiple'">
      </TextField>

      <SelectField :title="translations.generic.type" :changed="selectedTypeChanged" :options="typeOptions"
        :disabled="operation != 'create'" v-model:model-value="selectedType">
      </SelectField>

      <TextField :title="translations.generic.code" :changed="codeChanged" v-model:model-value="code">
      </TextField>

      <TextareaField :title="translations.generic.description" :change="descriptionChanged"
        v-model:model-value="description" class="description-field">
      </TextareaField>

      <ColorPickerField :changed="colorChanged" v-model:model-value="color"></ColorPickerField>

      <TextField :title="translations.generic.quality" :changed="qualityChanged" v-model:model-value="quality">
      </TextField>

      <SelectField :title="translations.generic.productGroup" :changed="selectedProductGroupChanged" :options="groups"
        :clear-btn="true" v-model:model-value="selectedProductGroup">
      </SelectField>

      <Fieldset class="log-fields" :legend="translations.products.logData.logData"
        v-if="selectedType.value === ProductType.Log">

        <div class="toggle-field span-2" :class="{ changed: isSortedChanged }">
          <SelectButton id="edit-product-dialog-is-sorted" v-model="isSorted" :options="isSortedOptions"
            optionValue="value" optionLabel="label" :allowEmpty="false" fluid :class="{ changed: isSortedChanged }"
            class="custom-selectbutton" />
        </div>

        <NumberField :title="translations.products.logData.logsPerSquareMeter" :changed="logsPerSquareMeterChanged"
          :error="logsPerSquareMeterError" :unit="translations.products.logsPerSquareMeter"
          :description="translations.products.logData.logsPerSquareMeterDescription" :max-fraction-digits="3"
          :action-button="{ label: translations.generic.calculate, icon: 'pi pi-calculator', action: () => calcLogsPerM2() }"
          v-model:model-value="logsPerSquareMeter">
        </NumberField>

        <TextField :title="translations.products.logData.species" :changed="speciesChanged"
          v-model:model-value="species">
        </TextField>

        <NumberField :title="translations.products.logData.minDiameter" :changed="minDiameterChanged"
          :unit="translations.units.millimeters" v-model:model-value="minDiameter">
        </NumberField>

        <NumberField :title="translations.products.logData.maxDiameter" :changed="maxDiameterChanged"
          :unit="translations.units.millimeters" v-model:model-value="maxDiameter">
        </NumberField>

        <NumberField :title="translations.products.logData.minLength" :changed="minLengthChanged"
          :unit="translations.units.millimeters" v-model:model-value="minLength">
        </NumberField>

        <NumberField :title="translations.products.logData.maxLength" :changed="maxLengthChanged"
          :unit="translations.units.millimeters" v-model="maxLength">
        </NumberField>

        <NumberField :title="translations.products.logData.averageCylinderVolume"
          :changed="averageCylinderVolumeChanged" :unit="translations.units.cubicMeter" v-model="averageCylinderVolume"
          :max-fraction-digits="3">
        </NumberField>

        <NumberField :title="translations.products.logData.averagePhysicalVolume"
          :changed="averagePhysicalVolumeChanged" :unit="translations.units.cubicMeter"
          v-model:model-value="averagePhysicalVolume" :max-fraction-digits="3">
        </NumberField>

        <TextField :title="translations.products.logData.logSorterName" :changed="logSorterNameChanged"
          v-model:model-value="logSorterName">
        </TextField>

        <TextField :title="translations.products.logData.sawLineName" :changed="sawLineNameChanged"
          v-model:model-value="sawLineName">
        </TextField>
      </Fieldset>
      <Fieldset class="coil-fields" :legend="translations.products.coilData.title"
        v-if="selectedType.value === ProductType.Coil">

        <NumberField :title="translations.products.coilData.diameter" :changed="diameterChanged"
          :max-fraction-digits="3" :unit="translations.units.millimeters" v-model:model-value="diameter">
        </NumberField>

        <NumberField :title="translations.products.coilData.depth" :changed="depthChanged" :max-fraction-digits="3"
          :unit="translations.units.millimeters" v-model:model-value="depth">
        </NumberField>

      </Fieldset>
      <Fieldset class="bulk-fields" :legend="translations.products.bulkData.title"
        v-if="selectedType.value === ProductType.Bulk">

        <NumberField :title="translations.products.bulkData.density" :changed="bulkDensityChanged"
          :max-fraction-digits="3" :unit="translations.units.kgPerM3" v-model:model-value="bulkDensity"
          :error="densityError" :min="0.1">
        </NumberField>

        <SelectField :title="translations.generic.unit" :changed="bulkUnitChanged" :options="bulkUnitOptions"
          :options-value="'value'" v-model:model-value="bulkUnit">
        </SelectField>

      </Fieldset>

      <NumberField v-if="selectedType.value === ProductType.Dimension"
        :title="translations.products.dimensionData.moisture" :changed="dimensionChanged" :max-fraction-digits="3"
        :unit="translations.units.percent" v-model:model-value="dimMoisture">
      </NumberField>

      <Fieldset class="actual-fields" :legend="translations.products.dimensionData.actualDimensions"
        v-if="selectedType.value === ProductType.Dimension">

        <NumberField :title="translations.products.dimensionData.density" :changed="dimensionChanged"
          :max-fraction-digits="3" :unit="translations.units.kgPerM3" :min="0" v-model:model-value="dimDensity">
        </NumberField>

        <div class="field" :class="{ changed: dimensionChanged }">
          <label>{{ translations.products.dimensionData.thickness }}</label>
          <MeasurementInput :modelValue="dimThickness ?? null" @update:modelValue="(v) => dimThickness = v ?? undefined"
            unitKey="dimension-thickness" :min="0"></MeasurementInput>
        </div>

        <div class="field" :class="{ changed: dimensionChanged }">
          <label>{{ translations.products.dimensionData.width }}</label>
          <MeasurementInput :modelValue="dimWidth ?? null" @update:modelValue="(v) => dimWidth = v ?? undefined"
            unitKey="dimension-width" :min="0"></MeasurementInput>
        </div>

        <div class="field" :class="{ changed: dimensionChanged }">
          <label>{{ translations.products.dimensionData.length }}</label>
          <MeasurementInput :modelValue="dimLength ?? null" @update:modelValue="(v) => dimLength = v ?? undefined"
            unitKey="dimension-length" :min="0"></MeasurementInput>
        </div>

      </Fieldset>

      <Fieldset class="nominal-fields" :legend="translations.products.dimensionData.nominalDimensions"
        v-if="selectedType.value === ProductType.Dimension">

        <NumberField :title="translations.products.dimensionData.density" :changed="dimensionChanged"
          :max-fraction-digits="3" :unit="translations.units.kgPerM3" :min="0"
          v-model:model-value="dimNominalDensity">
        </NumberField>

        <div class="field" :class="{ changed: dimensionChanged }">
          <label>{{ translations.products.dimensionData.thickness }}</label>
          <MeasurementInput :modelValue="dimNominalThickness ?? null"
            @update:modelValue="(v) => dimNominalThickness = v ?? undefined" unitKey="dimension-nominal-thickness"
            :min="0"></MeasurementInput>
        </div>

        <div class="field" :class="{ changed: dimensionChanged }">
          <label>{{ translations.products.dimensionData.width }}</label>
          <MeasurementInput :modelValue="dimNominalWidth ?? null"
            @update:modelValue="(v) => dimNominalWidth = v ?? undefined" unitKey="dimension-nominal-width" :min="0">
          </MeasurementInput>
        </div>

        <div class="field" :class="{ changed: dimensionChanged }">
          <label>{{ translations.products.dimensionData.length }}</label>
          <MeasurementInput :modelValue="dimNominalLength ?? null"
            @update:modelValue="(v) => dimNominalLength = v ?? undefined" unitKey="dimension-nominal-length" :min="0">
          </MeasurementInput>
        </div>

      </Fieldset>
    </div>
    <div class="footer-field">
      <ProgressBar v-if="showProgressBar" class="progress-bar"
        :value="Math.round(((progress / editProducts.length) * 100))"></ProgressBar>
      <Button @click="onSubmit" :disabled="!saveEnabled" class="submit-button" :label="translations.generic.save"
        type="submit" :loading="loading"></Button>
    </div>
  </Dialog>
</template>

<style scoped>
.product-form {
  display: grid;
  row-gap: .5rem;
  column-gap: .5rem;
  grid-template-columns: repeat(3, 1fr);
}

.description-field {
  grid-column: 1/4;
}

.field {
  display: flex;
  flex-direction: column;
}

.footer-field {
  margin-top: 10px;
  display: grid;
  grid-template-columns: 75% 1fr;
  column-gap: 2rem;
  align-items: center;
}

.progress-bar {
  grid-column: 1;
}

.submit-button {
  grid-column: 2;
}

.log-fields {
  grid-column: 1/4;
}

.log-fields :deep(.p-fieldset-content) {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: .5rem;
  column-gap: .5rem;
}

.log-fields :deep(.span-2) {
  grid-column: span 2;
}

.toggle-field :deep(.span-2) {
  grid-column: span 2;
}

.coil-fields {
  grid-column: 1/4;
}

.coil-fields :deep(.p-fieldset-content) {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: .5rem;
  column-gap: .5rem;
}

.bulk-fields {
  grid-column: 1/4;
}

.bulk-fields :deep(.p-fieldset-content) {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: .5rem;
  column-gap: .5rem;
}

.actual-fields {
  grid-column: 1/4;
}

.actual-fields :deep(.p-fieldset-content) {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: .5rem;
  column-gap: .5rem;
}

.nominal-fields {
  grid-column: 1/4;
}

.nominal-fields :deep(.p-fieldset-content) {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: .5rem;
  column-gap: .5rem;
}

.field :deep(.p-inputnumber-input) {
  text-align: right;
}

.toggle-field {
  display: flex;
  align-items: end;
  column-gap: .5rem;
}

.custom-selectbutton :deep(.p-togglebutton-checked .p-togglebutton-content) {
  background-color: var(--p-primary-500) !important;
  color: white;
}

.p-dark .custom-selectbutton :deep(.p-togglebutton-checked .p-togglebutton-content) {
  background-color: var(--p-primary-400) !important;
  color: black;
}

.custom-selectbutton.changed :deep(.p-togglebutton-checked .p-togglebutton-content) {
  background-color: var(--p-yellow-100) !important;
  color: #000 !important;
}

.custom-selectbutton.changed :deep(.p-togglebutton-checked:hover .p-togglebutton-content) {
  background-color: var(--p-yellow-200) !important;
}

.p-dark .custom-selectbutton.changed :deep(.p-togglebutton-checked .p-togglebutton-content) {
  background-color: var(--p-yellow-200) !important;
  color: #000 !important;
}

.p-dark .custom-selectbutton.changed :deep(.p-togglebutton-checked:hover .p-togglebutton-content) {
  background-color: var(--p-yellow-100) !important;
}

.custom-selectbutton :deep(.p-togglebutton) {

  transition: background 0.2s, border-color 0.2s;
}
</style>

<style>
.p-progressbar-determinate .p-progressbar-value {
  transition: width 10ms linear !important;
}
</style>
